import { DynamoDBClient } from '@aws-sdk/client-dynamodb';
import { DynamoDBDocumentClient, PutCommand, GetCommand, UpdateCommand, ScanCommand, DeleteCommand } from '@aws-sdk/lib-dynamodb';
import { fetchAuthSession } from 'aws-amplify/auth';
import mockDataService from './mockDataService';

// Check if we should use mock data
const shouldUseMockData = () => {
  // STRICT PRODUCTION CHECK: Never use mock data in production
  const isProduction = process.env.NODE_ENV === 'production' || 
                      (typeof window !== 'undefined' && (
                        window.location.hostname === 'getcertifiable.com' ||
                        window.location.hostname === 'www.certifiable.me' ||
                        window.location.hostname.includes('amplifyapp.com')
                      ));
  
  if (isProduction) {
    return false;
  }
  
  // Force mock data if emergency access is being used
  if (typeof window !== 'undefined' && localStorage.getItem('force_mock_data') === 'true') {
    return true;
  }
  
  return process.env.REACT_APP_FORCE_MOCK_DATA === 'true' || 
         (process.env.NODE_ENV === 'development' && !process.env.REACT_APP_COGNITO_USER_POOL_ID);
};

// Function to get DynamoDB client with Cognito credentials
const getDynamoClient = async () => {
  try {
    const session = await fetchAuthSession();
    
    if (!session.credentials) {
      throw new Error('No valid credentials found. User may not be authenticated.');
    }

    const client = new DynamoDBClient({
      region: process.env.REACT_APP_AWS_REGION || 'us-east-1',
      credentials: {
        accessKeyId: session.credentials.accessKeyId,
        secretAccessKey: session.credentials.secretAccessKey,
        sessionToken: session.credentials.sessionToken,
      },
    });

    return DynamoDBDocumentClient.from(client);
  } catch (error) {
    throw error;
  }
};

const USERS_TABLE = process.env.REACT_APP_DYNAMODB_USERS_TABLE || 'asl-ceu-tracker-users';
const CEUS_TABLE = process.env.REACT_APP_DYNAMODB_CEUS_TABLE || 'asl-ceu-tracker-ceus';

export const userService = {
  // Create or update user profile
  saveUserProfile: async (userData) => {
    try {
      // Check if this is a profile update (has userId but no email) vs new user creation
      const isProfileUpdate = userData.userId && !userData.email;
      const isNewUserCreation = userData.email;
      
      if (isNewUserCreation) {
        // CRITICAL: Validate required fields for NEW USER creation
        if (!userData.email || userData.email.trim() === '') {
          console.error('❌ CRITICAL: Attempting to create new user without email:', userData.userId);
          return { success: false, error: 'Email is required for user creation' };
        }

      } else if (isProfileUpdate) {
        // Profile update - email not required, will be preserved from existing record

      } else {
        console.error('❌ CRITICAL: Invalid user data - no userId or email provided');
        return { success: false, error: 'User ID or email is required' };
      }

      if (!userData.userId || userData.userId.trim() === '') {
        console.error('❌ CRITICAL: Attempting to save user without userId');
        return { success: false, error: 'User ID is required for user profile operations' };
      }
      
      const docClient = await getDynamoClient();
      
      // First, get existing user data to preserve admin fields
      let existingUser = {};
      try {
        const getCommand = new GetCommand({
          TableName: USERS_TABLE,
          Key: { userId: userData.userId }
        });
        const result = await docClient.send(getCommand);
        existingUser = result.Item || {};
      } catch (error) {
      }
      
      const command = new PutCommand({
        TableName: USERS_TABLE,
        Item: {
          userId: userData.userId,
          username: userData.username,
          name: userData.name || `${userData.firstName || userData.givenName || ''} ${userData.lastName || userData.familyName || ''}`.trim() || existingUser.name || '',
          email: userData.email || existingUser.email, // Preserve existing email for profile updates
          firstName: userData.firstName || userData.givenName, // Support both for migration
          lastName: userData.lastName || userData.familyName, // Support both for migration
          state: userData.state || existingUser.state || '', // ADD STATE FIELD
          certifyingBody: userData.certifyingBody || existingUser.certifyingBody || 'RID',
          membershipType: userData.membershipType || existingUser.membershipType || '',
          memberNumber: userData.memberNumber || existingUser.memberNumber || '',
          currentCycleStart: userData.currentCycleStart || existingUser.currentCycleStart || null,
          currentCycleEnd: userData.currentCycleEnd || existingUser.currentCycleEnd || null,
          profilePhotoUrl: userData.profilePhotoUrl || existingUser.profilePhotoUrl || '', // ADD PROFILE PHOTO FIELD
          stateLicenses: userData.stateLicenses || existingUser.stateLicenses || [], // INITIALIZE STATE LICENSES ARRAY
          profileCompleted: userData.profileCompleted !== undefined ? userData.profileCompleted : (existingUser.profileCompleted || false),
          // PRESERVE ADMIN FIELDS
          isAdmin: existingUser.isAdmin || false,
          role: existingUser.role || 'user',
          adminPermissions: existingUser.adminPermissions || [],
          // PRESERVE OTHER IMPORTANT FIELDS
          syncedFromCognito: existingUser.syncedFromCognito,
          isActive: existingUser.isActive,
          createdAt: existingUser.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
      });

      await docClient.send(command);
            return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Enhanced getUserProfile - supports email OR userId lookup
  getUserProfile: async (identifier) => {
    // Use mock data if flag is set
    if (shouldUseMockData()) {
      return mockDataService.getUser(identifier);
    }

    try {
      const docClient = await getDynamoClient();
      
      // Try direct userId lookup first (fastest)
      if (!identifier.includes('@')) {
        const command = new GetCommand({
          TableName: USERS_TABLE,
          Key: { userId: identifier },
        });

        const response = await docClient.send(command);
        if (response.Item) {
          return { success: true, data: response.Item };
        }
      }
      
      // Email lookup - scan for matching email
      const scanCommand = new ScanCommand({
        TableName: USERS_TABLE,
        FilterExpression: 'email = :email',
        ExpressionAttributeValues: {
          ':email': identifier
        }
      });

      const response = await docClient.send(scanCommand);
      
      if (response.Items && response.Items.length > 0) {
        // Return the most complete profile if multiple exist
        const bestProfile = response.Items.reduce((best, current) => {
          const bestScore = (best.profileCompleted ? 50 : 0) + (best.memberNumber ? 25 : 0) + (best.firstName ? 25 : 0);
          const currentScore = (current.profileCompleted ? 50 : 0) + (current.memberNumber ? 25 : 0) + (current.firstName ? 25 : 0);
          return currentScore > bestScore ? current : best;
        });
        
        return { success: true, data: bestProfile };
      }
      
      return { success: false, error: 'User profile not found' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Update specific profile fields
  updateUserProfile: async (userId, updates) => {
    try {
      const docClient = await getDynamoClient();
      
      const updateExpression = [];
      const expressionAttributeNames = {};
      const expressionAttributeValues = {};

      Object.keys(updates).forEach((key, index) => {
        // Skip empty strings as DynamoDB doesn't allow them
        // But allow null values (DynamoDB accepts null)
        if (updates[key] === '') return;
        
        const attributeName = `#attr${index}`;
        const attributeValue = `:val${index}`;
        
        updateExpression.push(`${attributeName} = ${attributeValue}`);
        expressionAttributeNames[attributeName] = key;
        expressionAttributeValues[attributeValue] = updates[key];
      });

      // Only add updatedAt if it's not already in the updates
      if (!updates.updatedAt) {
        const updatedAtIndex = Object.keys(updates).length;
        updateExpression.push(`#updatedAt${updatedAtIndex} = :updatedAt${updatedAtIndex}`);
        expressionAttributeNames[`#updatedAt${updatedAtIndex}`] = 'updatedAt';
        expressionAttributeValues[`:updatedAt${updatedAtIndex}`] = new Date().toISOString();
      }

      const command = new UpdateCommand({
        TableName: USERS_TABLE,
        Key: { userId },
        UpdateExpression: `SET ${updateExpression.join(', ')}`,
        ExpressionAttributeNames: expressionAttributeNames,
        ExpressionAttributeValues: expressionAttributeValues,
        ReturnValues: 'ALL_NEW',
      });

      const response = await docClient.send(command);
            return { success: true, data: response.Attributes };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Mark profile as completed
  markProfileCompleted: async (userId) => {
    return await userService.updateUser(userId, {
      profileCompleted: true,
    });
  },

  // Get all users (admin only)
  getAllUsers: async () => {
    // ACCOUNT LINKING FIX: Always use real data for account linking
    // Mock data doesn't contain real user accounts needed for linking
    try {
      const docClient = await getDynamoClient();
      
      const command = new ScanCommand({
        TableName: USERS_TABLE,
      });

      const response = await docClient.send(command);
      return { success: true, users: response.Items || [] };
    } catch (error) {
      console.error('Error getting all users:', error);
      // Return a proper error response instead of undefined
      return { success: false, error: error.message, users: [] };
    }
  },

  // Get user CEUs (for account deletion)
  getUserCEUs: async (userId) => {
    try {
      const docClient = await getDynamoClient();
      
      const command = new ScanCommand({
        TableName: CEUS_TABLE,
        FilterExpression: '#userId = :userId',
        ExpressionAttributeNames: {
          '#userId': 'userId'
        },
        ExpressionAttributeValues: {
          ':userId': userId
        }
      });

      const response = await docClient.send(command);
      
      return {
        success: true,
        ceus: response.Items || []
      };
    } catch (error) {
      console.error('Error getting user CEUs:', error);
      return { success: false, error: error.message, ceus: [] };
    }
  },

  // Delete user profile (for account deletion)
  deleteUserProfile: async (userId) => {
    try {
      const docClient = await getDynamoClient();
      
      const command = new DeleteCommand({
        TableName: USERS_TABLE,
        Key: { userId }
      });

      await docClient.send(command);
      
      return { success: true, message: 'User profile deleted' };
    } catch (error) {
      console.error('Error deleting user profile:', error);
      return { success: false, error: error.message };
    }
  },

  // Delete CEU record (for account deletion)
  deleteCEU: async (userId, ceuId) => {
    try {
      const docClient = await getDynamoClient();
      
      const command = new DeleteCommand({
        TableName: CEUS_TABLE,
        Key: { userId, ceuId }
      });

      await docClient.send(command);
      
      return { success: true, message: 'CEU deleted' };
    } catch (error) {
      console.error('Error deleting CEU:', error);
      return { success: false, error: error.message };
    }
  },
};

export default userService;
